import { z } from 'zod';

import type { CalendarDate, Instant, TimeZone } from '@chariot/core-domain';
import { brandId } from '@chariot/core-ids';

/**
 * Mutually exclusive by construction: `'2026-06-18'` fails `instantSchema` and
 * `'2026-06-18T00:00:00Z'` fails `calendarDateSchema`, so a field declared with
 * the wrong one fails at the boundary rather than shifting by a day later.
 */

/** `z.iso.date()` rejects a day that does not exist (`2026-02-30`), not merely one shaped wrongly. */
export const calendarDateSchema: z.ZodType<CalendarDate, string> = z.iso
  .date()
  .transform((value) => brandId('CalendarDate', value));

/** `z.iso.datetime()` accepts only a `Z` suffix, so an offset such as `+07:00` is rejected rather than normalized. */
export const instantSchema: z.ZodType<Instant, string> = z.iso
  .datetime()
  .transform((value) => brandId('Instant', value));

/** `Intl.supportedValuesOf('timeZone')` cannot be the allowlist: `UTC` is not among the names it returns. */
function canonicalTimeZone(value: string): string | undefined {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: value }).resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

/**
 * `Intl` accepts deprecated aliases, so `US/Eastern` and `America/New_York` are
 * one zone that compares unequal as strings.
 *
 * Fixed offsets are accepted by `Intl` and rejected here: an offset has no
 * daylight-saving rules, so a range spanning a transition lands an hour out.
 */
export const timeZoneSchema: z.ZodType<TimeZone, string> = z.string().transform((value, ctx) => {
  const canonical = canonicalTimeZone(value);

  if (canonical === undefined) {
    ctx.addIssue({ code: 'custom', message: `Unknown timezone: ${value}` });
    return z.NEVER;
  }

  if (canonical.startsWith('+') || canonical.startsWith('-')) {
    ctx.addIssue({
      code: 'custom',
      message: `Fixed offsets have no daylight-saving rules; use a named zone instead of ${value}`,
    });
    return z.NEVER;
  }

  return brandId('TimeZone', canonical);
});

/** The calendar basis, named because a literal `'UTC'` is banned: it is how an instant field gets pinned to UTC by accident. */
export const UTC: TimeZone = brandId('TimeZone', 'UTC');
